import React, { useCallback } from 'react';
import styled from 'styled-components';
import ContentBaseLayout from '../shared/ContentBaseLayout';
import ProductList from '../../../shared/common/ProductList';
import LibxchangeProductElement from '../shared/LibxchangeProductElement';
import { Pagination } from '../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import { LocalRegionsSidebar } from './LocalRegionsSidebar';
import { useParams } from 'react-router';
import {
    invalidateLocalRegionProducts,
    useLocalRegionProducts
} from '../../../api/local-content/marketplace/local-regions/useLocalRegionProducts';
import { useLocalRegions } from '../../../api/local-content/marketplace/local-regions/useLocalRegions';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { FilterBar } from '../../../shared/common/FilterBar';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLocalRegionsAggregationsQuickSearch } from '../../../api/local-content/marketplace/local-regions/fetchLocalRegionsAggregationsQuickSearch';
import BreadCrumbNav from '../../../shared/navigation/BreadcrumbNav';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLocalRegionsAggregations } from '../../../api/local-content/marketplace/local-regions/fetchLocalRegionsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { useDefaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import { URL_LIBX_ROUTE } from '../constants';
import { useTranslation } from 'react-i18next';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../shared/common/displayType/types';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { BreadcrumbsState } from '../../../shared/breadcrumbs/types';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';

const StFilterBar = styled(FilterBar)`
    padding: 25px 0;
`;

export const LocalRegionsPage = () => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { level1, level2, shopLanguage } = useParams<'level1' | 'level2' | 'shopLanguage'>();
    const selectedRegion = [level1, level2].filter(val => !!val).join('->');

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultPageSize: 55,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam(DISPLAY_TYPE_MINIMAL);

    const { localRegions, isLoading: isLocalRegionsLoading } = useLocalRegions(ageCategory, shopLanguage);

    const { mutate: addProductToCollection } = useAddLibxProductToCollection();

    const {
        products,
        totalHits,
        isInitialLoading: isProductsLoading
    } = useLocalRegionProducts({
        pagination: { sortBy, page, pageSize },
        filterMap: productFilter,
        ageCategory,
        region: selectedRegion,
        shopLanguage
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useDefaultVisibleFilterGroups(ageCategory);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'localRegionsPage',
            selectedRegion,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, selectedRegion, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchLocalRegionsAggregations({
                ...options,
                ageCategory,
                region: selectedRegion,
                shopLanguage
            }),
        [ageCategory, selectedRegion, shopLanguage]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchLocalRegionsAggregationsQuickSearch({ ...options, ageCategory, shopLanguage }),
        [ageCategory, shopLanguage]
    );
    //endregion

    const breadcrumbRemap = localRegions.map(region => ({
        key: region.name,
        value: region.name,
        docCount: region.docCount,
        buckets: region.level2?.map(level2 => ({
            key: !selectedRegion.includes(level2.name!) ? `${level1}->${level2.name}` : selectedRegion,
            value: level2.name,
            docCount: level2.docCount ?? 0
        }))
    }));

    const breadcrumbsState: BreadcrumbsState = {
        key: 'by-region',
        label: 'Local Regions',
        buckets: (breadcrumbRemap as any) || [],
        activeBucket: selectedRegion
    };

    const loading = isLocalRegionsLoading || isProductsLoading;

    function handleAddToCollection(productId: string) {
        addProductToCollection(productId, {
            onSuccess: () => {
                invalidateLocalRegionProducts(selectedRegion).then();
            }
        });
    }

    const libxPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}` : URL_LIBX_ROUTE;

    return (
        <ContentBaseLayout
            documentTitle="Local Regions"
            topComponent={
                <>
                    <BreadCrumbNav
                        breadcrumbs={breadcrumbsState}
                        fixedLabel={t('menu.shop.libxchange.localRegions')}
                        path={libxPath}
                        customGenreBase="localregions"
                        floatingClass=""
                    />
                    <StFilterBar
                        totalProducts={totalHits}
                        sortBy={sortBy}
                        pageSize={pageSize}
                        displayType={display}
                        hideSelectButtons={true}
                        hideGroupBy={true}
                        excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                        excludeSorting={[
                            'narratorAZ',
                            'stock',
                            'reserve',
                            'durationLongToShort',
                            'durationShortToLong',
                            'priceHighToLow',
                            'priceLowToHigh'
                        ]}
                    />
                </>
            }
            sidebarComponent={
                displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                        isLocalContent={true}
                    />
                ) : (
                    <LocalRegionsSidebar
                        localRegions={localRegions}
                        selectedRegion={selectedRegion}
                        shopLanguage={shopLanguage}
                    />
                )
            }
            contentComponent={
                !loading && products.length === 0 ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main">
                        <div className="ca-main-inner">
                            <ProductList
                                products={products as ProductMetaData[]}
                                displayType={display}
                                onAddPrice={async () => {}}
                            >
                                {({ key, isListView, ...value }) => (
                                    <LibxchangeProductElement
                                        {...value}
                                        key={key}
                                        displayType={display}
                                        onAddToCollection={handleAddToCollection}
                                        shopLanguage={shopLanguage}
                                    />
                                )}
                            </ProductList>
                            <Pagination totalItems={totalHits} pageSize={pageSize} />
                        </div>
                    </div>
                )
            }
            loading={loading}
        />
    );
};
